/**
 * Wire constants for the WEB account write-state gate (issue #2716).
 *
 * Dependency-free on purpose — `lib/error-message.ts` (a pure module shared
 * by client components) maps the wire code to the human sentence, and the
 * server-side gate (`lib/account-write-state.ts`, Prisma-backed) builds the
 * refusal from the same constants. Mirrors `lib/sso/constants.ts`.
 *
 * A dunning-downgraded account (Starter, still `past_due` / `unpaid`) or a
 * cancelled subscription (starter + `canceled`) is READ_ONLY on the web
 * exactly as it is on mobile (`derivePlanState` → READ_ONLY_ACCOUNT; a PAID
 * plan whose renewal Stripe is still retrying is writable — Alec,
 * 2026-09-29): every cookie-authenticated mutation that reaches
 * `requireAuth()` and is not on the exemption list is refused with this
 * contract, reads stay open, and the dunning / cancelled banner explains why.
 */

/**
 * The LEGACY `error` token the gate answered with before the body carried the
 * human sentence in `error` (#2716 review F7). Still recognised by
 * `isAccountReadOnlyError` (a sink or cached client may forward it), never
 * emitted any more.
 */
export const ACCOUNT_READ_ONLY_ERROR = "account_read_only";

/**
 * THE machine contract: every read-only refusal carries this `code` (and a
 * `reason`). Clients and tests key on `code`, never on `error` — `error` is
 * the human sentence, so the many web sinks that render `data.error` raw
 * (`setError(data.error)`, `toast.error(body.error)`) show the dunning
 * sentence without a per-sink change.
 */
export const ACCOUNT_READ_ONLY_CODE = "ACCOUNT_READ_ONLY";

export type AccountReadOnlyReason = "past_due" | "cancelled";

/**
 * The sentence a person sees. The banner-specific variants name the fix the
 * banner offers; the generic one is what `getErrorMessage` shows for a sink
 * that only forwarded the bare `error` code.
 */
export const ACCOUNT_READ_ONLY_MESSAGES: Record<
  AccountReadOnlyReason | "generic",
  string
> = {
  past_due:
    "Your account is read-only until its outstanding payment is settled. The account owner can update the card from the billing banner or Settings → Billing to unlock changes.",
  cancelled:
    "Your account is read-only because its subscription was cancelled. Reactivate under Settings → Billing to make changes again.",
  generic:
    "Your account is read-only until its billing is sorted. The account owner can fix it from the billing banner or Settings → Billing.",
};

export function accountReadOnlyMessage(
  reason: AccountReadOnlyReason | null | undefined,
): string {
  return ACCOUNT_READ_ONLY_MESSAGES[reason ?? "generic"];
}

/**
 * The 403 body shape every gated web mutation returns for a read-only account.
 * `error` and `message` are the same human sentence (`error` so raw
 * `data.error` sinks render it; `message` for the sinks that read that key);
 * `code` + `reason` are the machine contract.
 */
export interface AccountReadOnlyErrorBody {
  error: string;
  code: typeof ACCOUNT_READ_ONLY_CODE;
  reason: AccountReadOnlyReason;
  message: string;
}

export function accountReadOnlyBody(
  reason: AccountReadOnlyReason,
): AccountReadOnlyErrorBody {
  const message = accountReadOnlyMessage(reason);
  return { error: message, code: ACCOUNT_READ_ONLY_CODE, reason, message };
}

/**
 * True for a parsed JSON error body produced by the gate (keyed on `code`),
 * or for the legacy bare `account_read_only` token / legacy body. Client sinks
 * use this to decide whether to show the dunning sentence instead of their
 * own hard-coded meaning of a 403.
 */
export function isAccountReadOnlyError(value: unknown): boolean {
  if (value === ACCOUNT_READ_ONLY_ERROR) return true;
  if (value && typeof value === "object") {
    const { error, code } = value as { error?: unknown; code?: unknown };
    return code === ACCOUNT_READ_ONLY_CODE || error === ACCOUNT_READ_ONLY_ERROR;
  }
  return false;
}
